"use client";
import React, { useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Sector,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { BsPeopleFill } from "react-icons/bs";
import "./charts.css";
import axios from "axios";

const GENDER_COLORS = {
  Male: "#e11d48", // Themed Crimson Red
  MALE: "#e11d48",
  Female: "#fda4af", // Themed Soft Pink/Rose
  FEMALE: "#fda4af",
  Others: "#be123c", // Dark Red
  OTHERS: "#be123c",
  default: "#e11d48", // Crimson Red
};

const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 8}
        outerRadius={outerRadius + 11}
        fill={fill}
        opacity={0.3}
      />
    </g>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const color =
      GENDER_COLORS[data.name.toUpperCase()] || GENDER_COLORS.default;
    return (
      <div
        className="custom-tooltip"
        style={{ borderLeft: `4px solid ${color}` }}
      >
        <p className="tooltip-label" style={{ color: color }}>
          {data.name}
        </p>
        <p className="tooltip-value">
          <span className="tooltip-value-label">Donors: </span>
          <span className="tooltip-value-number" style={{ color: "#1f2937" }}>
            {data.value}
          </span>
        </p>
      </div>
    );
  }
  return null;
};

const GenderChart = ({ college }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [donorData, setDonorData] = useState([]);
  const port = process.env.REACT_APP_SERVER_PORT;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await axios.get(
          port + `count-by-gender?College=${college}`,
        );

        const actualResult = Object.entries(result.data)
          .filter(([key]) => key && key.toLowerCase() !== "unknown")
          .map(([key, value]) => ({
            name: key.toUpperCase(),
            value: value,
          }));

        setDonorData(actualResult);
      } catch (error) {
        console.error("Error fetching gender data:", error);
      }
    };

    fetchData();
  }, [college, port]);

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const totalDonors = donorData.reduce(
    (sum, item) => sum + (item.value || 0),
    0,
  );

  return (
    <div className="chart-col">
      <div className="graph-card">
        {/* Custom Card Header */}
        <div className="graph-card-header">
          <div className="graph-card-title-container">
            <div
              className="graph-icon-wrapper"
              style={{ backgroundColor: "#ffebeb" }}
            >
              <BsPeopleFill
                className="graph-header-icon"
                style={{ color: "#ff0000" }}
              />
            </div>
            <span className="graph-card-title" style={{ color: "#ff0000" }}>
              Gender Distribution
            </span>
          </div>
        </div>

        {/* Graph wrapper */}
        <div
          className="chart-wrapper"
          style={{
            minHeight: "300px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {donorData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Tooltip content={<CustomTooltip />} />
                <Pie
                  activeIndex={activeIndex}
                  activeShape={renderActiveShape}
                  data={donorData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={75}
                  dataKey="value"
                  onMouseEnter={onPieEnter}
                  paddingAngle={3}
                >
                  {donorData.map((entry, index) => {
                    const color =
                      GENDER_COLORS[entry.name.toUpperCase()] ||
                      GENDER_COLORS.default;
                    return <Cell key={`cell-${index}`} fill={color} />;
                  })}
                </Pie>
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  iconType="circle"
                  iconSize={8}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div
              style={{
                textAlign: "center",
                color: "#6b7280",
                padding: "40px 0",
              }}
            >
              No Gender Data Available
            </div>
          )}
        </div>

        {/* Metrics/Legend Section */}
        <div
          className="metrics-container"
          style={{ background: "#fff5f5", marginTop: "10px" }}
        >
          <div className="metric-box">
            <div className="metric-details" style={{ alignItems: "center" }}>
              <span className="metric-label">Total Genders</span>
              <span className="metric-value" style={{ color: "#ff0000" }}>
                {donorData.length}
              </span>
            </div>
          </div>
          <div
            className="metric-divider"
            style={{ backgroundColor: "#ffe1e1" }}
          ></div>
          <div className="metric-box">
            <div className="metric-details" style={{ alignItems: "center" }}>
              <span className="metric-label">Total Donors</span>
              <span className="metric-value" style={{ color: "#ff0000" }}>
                {totalDonors}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenderChart;
